import {Injectable} from '@angular/core';
import {TreeViewItemsResponseObject} from './objekt-data.service';
import {UserAuthenticationService} from './user-authentication.service';
import {SwissAppConfigModels} from '../models/swissappconfig-models';
import {HttpClient} from '@angular/common/http';
import {HelperService} from './helper.service';
import {from, interval, ReplaySubject, Subject, Subscription, switchMap, tap} from 'rxjs';
import {ObjektModels} from '../models/objekt-models';
import {TreeviewNodeComponent} from '../home-layout/objekte/treeview/treeview-node/treeview-node.component';
import {Router} from '@angular/router';
import {LocalStorageService} from '../local-storage.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {SwissSettingsService} from './swiss-settings.service';
import {SWISS_WEB_CONFIG_DB_NAME} from "../models/local-storage-names";
import ObjektListViewItem = ObjektModels.ObjectListViewItem;
import SwissAppConfig = SwissAppConfigModels.SwissAppConfig;

@Injectable({
    providedIn: 'root',
})
export class TreeviewService {
    /**TREE**/
    treeData: TreeData | null = null;
    public treeData$: ReplaySubject<TreeData> = new ReplaySubject(1);
    private treeViewData: TreeViewItemsResponseObject<any>[] | null = null;
    private webServiceConfig?: SwissAppConfigModels.WebServiceConfig;
    private url = '';
    private _treeViewDataChanged = new Subject<TreeViewItemsResponseObject<any>[]>();
    private objectListData: ObjektListViewItem[] | null = null;
    private _objectListDataChanged = new Subject<ObjektListViewItem[]>();

    constructor(private userAuthService: UserAuthenticationService, private http: HttpClient, private helperService: HelperService, private route: Router, private localStorage: LocalStorageService, private swissSettings: SwissSettingsService) {
        this.helperService.getSwissWebServiceConfig$().pipe(takeUntilDestroyed(), tap((config) => {
            this.webServiceConfig = config;
            this.url = this.webServiceConfig!.objUrl;
        })).subscribe();
    }

    public static async createTree(value: TreeViewItemsResponseObject<any>[]): Promise<TreeData> {
        const objects: TreeNode[] = [];
        const groups: TreeNode[] = [];
        const searchListID: Map<number, TreeNode> = new Map<number, TreeNode>();
        const searchListObjectId: Map<string, TreeNode> = new Map<string, TreeNode>();
        value.forEach((responseObject) => {
            // remove false root nodes (send by webService) by ignoring objectID == null
            if (responseObject.objectID != null) {
                if (responseObject.pid) {
                    const treeNode = this.createNewTreeNode(responseObject, searchListObjectId, searchListID);

                    switch (responseObject.pid) {
                        case 1:
                            objects.push(treeNode);
                            break;
                        case 2:
                            groups.push(treeNode);
                            break;
                        default:
                            const parent = searchListID.get(treeNode.pid);
                            if (parent) {
                                parent.addChild(treeNode);
                            } else {
                                objects.push(treeNode);
                            }
                    }
                } else {
                    const treeNode = this.createNewTreeNode(responseObject, searchListObjectId, searchListID);
                    objects.push(treeNode);
                }
            }
        });
        return new TreeData(objects, groups, searchListObjectId);
    }

    private static createNewTreeNode(responseObject: TreeViewItemsResponseObject<any>, searchListObjectId: Map<string, TreeNode>, searchListID: Map<number, TreeNode>): TreeNode {
        const treeNode = new TreeNode(responseObject.id, responseObject.pid, responseObject.name || '', responseObject.objectID || '', responseObject.description || '', responseObject.imageURL || '', responseObject.hasChild, responseObject.color || undefined);
        searchListID.set(treeNode.id, treeNode);
        searchListObjectId.set(treeNode.objectID || '', treeNode);
        return treeNode;
    }

    async startTreeViewCache(): Promise<void> {
        // TODO remove in 2.18.290 https://swissjira.atlassian.net/browse/EN-4409
        const config = await this.localStorage.get<SwissAppConfig>('SwissAppConfig', SWISS_WEB_CONFIG_DB_NAME, false, undefined);
        if (this.swissSettings.getWebConfig().partialTreeMode as boolean && config?.objViewConfig) {
            await this.loadTreeData();
        }
        this.helperService.addPeriodicalCheck(this.reloadTree.bind(this));
    }

    public reloadTree(): Subscription {
        return interval(300000).pipe(
            switchMap(() => from(this.localStorage.get<SwissAppConfig | undefined>('SwissAppConfig', SWISS_WEB_CONFIG_DB_NAME, false, undefined)))
        ).subscribe((config) => {
            // TODO remove in 2.18.290 https://swissjira.atlassian.net/browse/EN-4409
            if (this.swissSettings.getWebConfig().partialTreeMode as boolean && config?.objViewConfig) {
                this.loadTreeData();
            }
        });
    }

    public getTreeViewDataChanged() {
        return this._treeViewDataChanged;
    }

    setData(value: TreeViewItemsResponseObject<any>[]) {
        if (this.treeViewData != value) {
            this.treeViewData = value;
            this._treeViewDataChanged.next(value);
        }
    }

    getData() {
        return this.treeViewData;
    }

    setObjectListData(value: ObjektListViewItem[]) {
        if (this.objectListData != value) {
            this.localStorage.add('offlineObjektBaum', value, 'SwissWebMainDB');
            this.objectListData = value;
            this._objectListDataChanged.next(value);
        }
    }

    getObjectListData() {
        return this.objectListData;
    }

    public getObjectListDataChanged() {
        return this._objectListDataChanged;
    }

    reloadFromCache() {
        this._treeViewDataChanged.next(this.treeViewData || []);
    }

    public async updateData() {
        await this.loadTreeData();
    }

    public addEntry(entry: TreeViewItemsResponseObject<any>) {
        const t = this.treeViewData?.concat([]) || [];
        t.push(entry);
        this.setData(t);
    }

    public deleteEntry(objectID) {
        const t = this.treeViewData?.concat([]) || [];
        const i = t.findIndex(o => o.objectID == objectID);
        if (i > -1) {
            t.splice(i, 1);
            this.setData(t);

        }
    }

    public async getSortedTreeBranchOfObject(objectID: string, applySorting = true): Promise<TreeData> {
        return new Promise(((resolve, reject) => {
            return this.http.get<TreeViewItemsResponseObject<any>[]>(this.url + `/GetSubObjectTreeDynamic?objectID=${objectID}&applySorting=${applySorting}`,
                this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo)
            ).subscribe({
                next: async (value) => {
                    resolve(await TreeviewService.createTree(value));
                },
                error: (err) => {
                    reject(err);
                }
            });
        }));
    }

    public async GetTreeDynamic(applySorting = true): Promise<TreeData> {
        return new Promise(((resolve, reject) => {
            return this.http.get<TreeViewItemsResponseObject<any>[]>(this.url + `/GetTreeDynamic?applySorting=${applySorting}`,
                this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo)
            ).subscribe({
                next: async (value) => {
                    resolve(await TreeviewService.createTree(value));
                },
                error: (err) => {
                    reject(err);
                }
            });
        }));
    }

    public getSubObjects(objectID: string): Promise<TreeData> {
        return new Promise(resolve => {
            this.http.get<any>(this.url + `/GetSubObjects?objectID=${objectID}`, this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo)).subscribe({
                next: async (value) => {
                    resolve(await TreeviewService.createTree(value));
                }
            });
        });
    }

    private loadTreeData(): Promise<any> {
        return new Promise(resolve => {
            this.http.get<TreeViewItemsResponseObject<any>[]>(this.url + '/GetObjektTree', this.helperService.getSwissHttpOptions(this.userAuthService.currentUserInfo)).subscribe({
                next: async (value) => {
                    this.setData(value);
                    await this.localStorage.add('offlineObjektBaum', value, 'SwissWebMainDB');
                    if (!this.treeData || !this.route.url.includes('/objekte')) {
                        this.treeData = await TreeviewService.createTree(value);
                        this.treeData$.next(this.treeData);
                    }
                    resolve({data: value, src: 'service'});
                }
            });
        });
    }
}

export class TreeData {
    objects: TreeNode[] | null = null;
    groups: TreeNode[] | null = null;
    search: Map<string, TreeNode> | null = null;

    constructor(objects: TreeNode[], groups: TreeNode[], search: Map<string, TreeNode>) {
        this.objects = objects;
        this.groups = groups;
        this.search = search;
    }
}

export class TreeNode {
    parent?: TreeNode;
    children: TreeNode[] = [];
    hasChild: boolean = false;
    depth: number = 0;
    element: TreeviewNodeComponent | null = null;

    pid: number;
    id: number;

    expanded: boolean = false;
    imageURL: string;
    name: string;
    description: string;
    objectID?: string;
    color?: string;

    constructor(id: number, pid: number, name: string, objectID: string, description: string, imageUrl: string, hasChildren: boolean = false, color: string | undefined = undefined) {
        this.id = id;
        this.pid = pid;
        this.name = name;
        this.objectID = objectID;
        this.description = description;
        this.imageURL = imageUrl;
        this.hasChild = hasChildren;
        this.color = color;
    }

    public addChild(child: TreeNode): void {
        this.hasChild = true;
        child.parent = this;
        child.depth = this.depth + 1;

        this.children.push(child);
    }
}

export interface TreeViewObjekt<T> extends TreeViewItemsResponseObject<T> {
    id: T;
    objectID: string;
    pid: T;
    name?: string;
    description?: string;
    objectNr?: string;
    hasChild: boolean;
    expanded: boolean;
    imageURL?: string;
    town?: string;
    zipCode?: string;
    projectLeader?: string;
    status?: string;
    note4?: string;
    mandator: string;
    companyPartID?: number;
}
